import { action, computed, IReactionDisposer, observable, reaction } from 'mobx';

import { ViewController } from '@estee/elc-service';

import { TranslationRepository } from '~repositories/TranslationRepository';
import { UserRepository } from '~repositories/UserRepository';
import { getInStoreDataFromStorage, getSocialProviderFromStorage } from '~utils/SocialUtils';
import { UserIdType } from '~domain/entities/Config';

interface IConsentFormViewController {
    translationRepository: TranslationRepository;
    userRepository: UserRepository;
}

export class ConsentFormViewController extends ViewController {
    @observable private userRepository: UserRepository;
    private disposeMobileChange: IReactionDisposer;
    private disposeEmailChange: IReactionDisposer;
    private disposePcEmailChange: IReactionDisposer;
    private translationRepository: TranslationRepository;

    constructor({ userRepository, translationRepository }: IConsentFormViewController) {
        super();
        this.translationRepository = translationRepository;
        this.userRepository = userRepository;
        this.disposeMobileChange = reaction(
            () => this.userRepository.userFormStore.values.mobileNumber,
            this.handleMobileChanged
        );
        this.disposeEmailChange = reaction(
            () => this.userRepository.userFormStore.values.emailAddress,
            this.handleEmailChanged
        );
        this.disposePcEmailChange = reaction(
            () => this.userRepository.userFormStore.values.pcEmailAddress,
            this.handleEmailChanged
        );
    }

    @action
    protected handleMobileChanged = async (): Promise<void> => {
        const { formFilled, setIsIdentityValueChanged } = this.userRepository.consentForm;
        if (formFilled) {
            setIsIdentityValueChanged(UserIdType.MOBILE_NUMBER);
            this.disposeMobileChange();
        }
    };

    @action
    protected handleEmailChanged = async (): Promise<void> => {
        const { formFilled, setIsIdentityValueChanged } = this.userRepository.consentForm;
        if (formFilled) {
            setIsIdentityValueChanged(UserIdType.EMAIL_ADDRESS);
            this.disposeEmailChange();
            this.disposePcEmailChange();
        }
    };

    @computed
    public get data() {
        const {
            consentForm: { isIdentityValueChanged, isSocialSignUpFlow, provider: providerName }
        } = this.userRepository;
        const {
            submitLabel,
            createAccountLabel,
            continueWithSocialConsent
        } = this.translationRepository.translations;
        const identityValueChanged =
            isIdentityValueChanged[UserIdType.MOBILE_NUMBER] ||
            isIdentityValueChanged[UserIdType.EMAIL_ADDRESS];
        const submitButtonText = isSocialSignUpFlow
            ? continueWithSocialConsent
            : identityValueChanged
            ? submitLabel
            : createAccountLabel;

        return {
            providerName,
            submitButtonText
        };
    }

    public loaded = async () => {
        const { store, receiptId } = getInStoreDataFromStorage();
        const providerName = getSocialProviderFromStorage();

        const {
            userFormStore,
            consentForm: { setProvider }
        } = this.userRepository;
        userFormStore?.setFieldValue('storeLocation', store);
        userFormStore?.setFieldValue('loyaltySignupPosTransId', receiptId);
        setProvider(providerName);
    };
}
